import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

const LS_SUIVI = "yoma_suivi_ventes_v1";
const LS_META = "yoma_appels_paiement_meta_v1";

const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const BG = "#F8F4EE";
const BORDER = "#E3D7C8";
const WHITE = "#FFFFFF";
const GREEN = "#5C8A5C";
const ORANGE = "#D68910";
const RED = "#B85C3A";

const FF_PLAYFAIR = "'Playfair Display', Georgia, serif";
const FF_DM = "'DM Sans', system-ui, sans-serif";

const DEFAULT_PCTS = [30, 25, 25, 15, 5];

const STATUTS_RELANCE = [
  { value: "en_attente", label: "En attente" },
  { value: "relance_1", label: "Relance 1" },
  { value: "relance_2", label: "Relance 2" },
  { value: "contentieux", label: "Contentieux" },
];

function montantPrevu(prixVente, pct) {
  const p = Number(prixVente) || 0;
  const c = Number(pct) || 0;
  return (p * c) / 100;
}

function formatMad(n) {
  if (n === "" || n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} MAD`;
}

function readSuivi() {
  try {
    const raw = localStorage.getItem(LS_SUIVI);
    if (!raw) return { projects: [], selectedProjectId: null, acquereurs: [] };
    return JSON.parse(raw);
  } catch {
    return { projects: [], selectedProjectId: null, acquereurs: [] };
  }
}

function writeSuivi(data) {
  try {
    localStorage.setItem(LS_SUIVI, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

function readMeta() {
  try {
    const raw = localStorage.getItem(LS_META);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function writeMeta(obj) {
  try {
    localStorage.setItem(LS_META, JSON.stringify(obj));
  } catch {
    /* ignore */
  }
}

function metaKey(acqId, vIndex) {
  return `${acqId}:${vIndex}`;
}

function montantRestantDu(a, v, idx) {
  const prev = montantPrevu(a.prixVente, v.pct ?? DEFAULT_PCTS[idx]);
  if (v.statut === "integral") return 0;
  const versé = Number(v.montantVerse) || 0;
  return Math.max(0, prev - versé);
}

/** Versement à faire apparaître dans les appels (non soldé) */
function versementEstOuvert(a, v, idx) {
  const reste = montantRestantDu(a, v, idx);
  if (reste <= 0.01) return false;
  if (v.statut === "integral") return false;
  if (v.statut === "non_paye" || v.statut === "partiel") return true;
  if (v.statut === "a_venir" && v.dateEcheance) {
    const d = new Date(v.dateEcheance);
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d <= t;
  }
  return false;
}

function joursRetard(dateEcheance) {
  if (!dateEcheance) return 0;
  const d = new Date(dateEcheance);
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diff = (t - d) / 86400000;
  return diff > 0 ? Math.floor(diff) : 0;
}

function getBankLines() {
  try {
    const raw = localStorage.getItem("yoma_vefa_dossier");
    if (!raw) return "Coordonnées bancaires : à communiquer par votre conseiller YOMA.";
    const d = JSON.parse(raw);
    const parts = [];
    if (d.banque) parts.push(`Banque : ${d.banque}`);
    if (d.agence) parts.push(`Agence : ${d.agence}`);
    if (d.iban) parts.push(`IBAN : ${d.iban}`);
    if (d.swift) parts.push(`SWIFT : ${d.swift}`);
    return parts.length ? parts.join("<br/>") : "Coordonnées bancaires : à communiquer par votre conseiller YOMA.";
  } catch {
    return "Coordonnées bancaires : à communiquer par votre conseiller YOMA.";
  }
}

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default function AppelsPaiementPage() {
  const [cabinet] = useState(() =>
    typeof localStorage !== "undefined" ? localStorage.getItem("yoma_company_name") || "YOMA DESIGN" : "YOMA DESIGN"
  );
  const [suivi, setSuivi] = useState(() => readSuivi());
  const [meta, setMeta] = useState(() => readMeta());
  const [modal, setModal] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailHtml, setEmailHtml] = useState("");
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  const refresh = useCallback(() => {
    setSuivi(readSuivi());
    setMeta(readMeta());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const id = "yoma-fonts-appels-paiement";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS_SUIVI || e.key === LS_META) refresh();
    };
    window.addEventListener("storage", onStorage);
    const onVis = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refresh]);

  const appels = useMemo(() => {
    const out = [];
    const acquereurs = suivi.acquereurs || [];
    acquereurs.forEach((a) => {
      (a.versements || []).forEach((v, idx) => {
        if (!versementEstOuvert(a, v, idx)) return;
        const prev = montantPrevu(a.prixVente, v.pct ?? DEFAULT_PCTS[idx]);
        const reste = montantRestantDu(a, v, idx);
        const jr = joursRetard(v.dateEcheance);
        out.push({
          acqId: a.id,
          acq: a,
          vIndex: idx,
          versement: v,
          labelV: `V${idx + 1}`,
          montantPrev: prev,
          montantDu: reste,
          dateEcheance: v.dateEcheance || "",
          joursRetard: jr,
        });
      });
    });
    return out;
  }, [suivi]);

  const kpis = useMemo(() => {
    const enAttente = appels.filter((x) => {
      const m = meta[metaKey(x.acqId, x.vIndex)] || {};
      return (m.suiviStatut || "en_attente") !== "contentieux";
    }).length;

    const retard30 = appels.filter((x) => x.joursRetard > 30).length;

    const totalAEncaisser = appels.reduce((s, x) => s + x.montantDu, 0);

    return { enAttente, retard30, totalAEncaisser };
  }, [appels, meta]);

  const updateMetaStatut = (acqId, vIndex, suiviStatut) => {
    const k = metaKey(acqId, vIndex);
    const next = { ...meta, [k]: { ...(meta[k] || {}), suiviStatut } };
    setMeta(next);
    writeMeta(next);
  };

  const pushHistorique = (acqId, vIndex, detail) => {
    const k = metaKey(acqId, vIndex);
    const cur = meta[k] || { suiviStatut: "en_attente", historique: [] };
    const historique = [...(cur.historique || []), { date: new Date().toISOString(), type: "email", detail }];
    const nextStatut =
      cur.suiviStatut === "en_attente"
        ? "relance_1"
        : cur.suiviStatut === "relance_1"
          ? "relance_2"
          : cur.suiviStatut;
    const next = { ...meta, [k]: { ...cur, suiviStatut: nextStatut, historique } };
    setMeta(next);
    writeMeta(next);
  };

  const openModalEmail = (item) => {
    const { acq, vIndex, labelV, montantDu, dateEcheance } = item;
    const n = vIndex + 1;
    const subject = `Appel à paiement — Villa ${acq.numVilla || "—"} — Versement ${n}`;
    const bank = getBankLines();
    const html = `
      <div style="font-family:Georgia,serif;max-width:560px;color:#3D2E20;line-height:1.65">
        <p>Bonjour ${escapeHtml([acq.civilite, acq.prenom, acq.nom].filter(Boolean).join(" ") || "Madame, Monsieur")},</p>
        <p>Nous vous rappelons qu'un montant reste dû au titre de votre acquisition (versement <strong>${labelV}</strong>) pour la villa <strong>${escapeHtml(acq.numVilla || "—")}</strong>.</p>
        <p><strong>Montant dû :</strong> ${formatMad(montantDu)}<br/>
        <strong>Date d'échéance :</strong> ${dateEcheance ? new Date(dateEcheance).toLocaleDateString("fr-FR") : "à confirmer"}</p>
        <p>${bank}</p>
        <p style="color:#8B7361;font-size:13px">Nous restons à votre disposition pour tout complément d'information.</p>
        <p style="color:#C09A5B;font-size:12px">${escapeHtml(cabinet)} — Promotion immobilière</p>
      </div>
    `;
    setSendTo(acq.email || "");
    setEmailSubject(subject);
    setEmailHtml(html);
    setModal(item);
    setSendError("");
  };

  const envoyerEmail = async () => {
    if (!sendTo.trim()) {
      setSendError("Indiquez une adresse email.");
      return;
    }
    setSending(true);
    setSendError("");
    try {
      const { error } = await supabase.functions.invoke("send-email", {
        body: { to: sendTo.trim(), subject: emailSubject, html: emailHtml },
      });
      if (error) throw error;
      pushHistorique(modal.acqId, modal.vIndex, `Rappel envoyé à ${sendTo.trim()}`);
      setModal(null);
    } catch (e) {
      setSendError(e?.message || String(e));
    } finally {
      setSending(false);
    }
  };

  const marquerRecu = (item) => {
    const { acqId, vIndex } = item;
    const data = readSuivi();
    const acq = data.acquereurs?.find((x) => x.id === acqId);
    if (!acq || !acq.versements?.[vIndex]) return;
    const prev = montantPrevu(acq.prixVente, acq.versements[vIndex].pct ?? DEFAULT_PCTS[vIndex]);
    const versements = acq.versements.map((v, i) =>
      i === vIndex
        ? {
            ...v,
            statut: "integral",
            montantVerse: prev,
            dateVersement: new Date().toISOString().slice(0, 10),
          }
        : v
    );
    const acquereurs = data.acquereurs.map((a) => (a.id === acqId ? { ...a, versements } : a));
    const next = { ...data, acquereurs };
    writeSuivi(next);
    setSuivi(next);
    const k = metaKey(acqId, vIndex);
    const m = { ...meta };
    delete m[k];
    setMeta(m);
    writeMeta(m);
  };

  return (
    <main style={{ minHeight: "100vh", background: BG, padding: 24, fontFamily: FF_DM, color: TAUPE }}>
      <header style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 12, color: "#8B7361", marginBottom: 6 }}>{cabinet}</div>
        <h1 style={{ margin: 0, fontFamily: FF_PLAYFAIR, fontSize: 32, fontWeight: 700 }}>Appels à Paiement</h1>
        <p style={{ margin: "8px 0 0", fontSize: 14, color: "#8B7361" }}>
          Gestion et envoi des appels de fonds acquéreurs
        </p>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginBottom: 20 }}>
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Appels en attente</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: ORANGE }}>{kpis.enAttente}</div>
        </div>
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Appels en retard (&gt; 30 jours)</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: RED }}>{kpis.retard30}</div>
        </div>
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Total à encaisser</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: GOLD }}>{formatMad(kpis.totalAEncaisser)}</div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {appels.length === 0 && (
          <div
            style={{
              background: WHITE,
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              padding: 32,
              textAlign: "center",
              color: "#8B7361",
            }}
          >
            Aucun versement ouvert — complétez le suivi des ventes ou marquez les échéances dans{" "}
            <strong>Suivi des Ventes</strong>.
          </div>
        )}

        {appels.map((item) => {
          const k = metaKey(item.acqId, item.vIndex);
          const m = meta[k] || {};
          const statut = m.suiviStatut || "en_attente";
          const nom = `${item.acq.prenom || ""} ${item.acq.nom || ""}`.trim() || "—";
          return (
            <article
              key={k}
              style={{
                background: WHITE,
                border: `1px solid ${BORDER}`,
                borderRadius: 12,
                padding: 18,
                borderLeft: `4px solid ${item.joursRetard > 30 ? RED : item.joursRetard > 0 ? ORANGE : GOLD}`,
                boxShadow: "0 4px 20px rgba(61,46,32,0.06)",
              }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 14 }}>
                <div>
                  <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 20, fontWeight: 700 }}>
                    {nom}{" "}
                    <span style={{ color: "#8B7361", fontSize: 14, fontWeight: 600 }}>— Villa {item.acq.numVilla || "—"}</span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13, color: "#8B7361" }}>
                    {item.labelV} · Échéance :{" "}
                    {item.dateEcheance ? new Date(item.dateEcheance).toLocaleDateString("fr-FR") : "Non définie"}
                  </div>
                  {item.joursRetard > 0 ? (
                    <span
                      style={{
                        display: "inline-block",
                        marginTop: 8,
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: "rgba(184,92,58,0.15)",
                        color: RED,
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      +{item.joursRetard} j. de retard
                    </span>
                  ) : null}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#8B7361", textTransform: "uppercase", letterSpacing: "0.06em" }}>Montant dû</div>
                  <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: GOLD }}>{formatMad(item.montantDu)}</div>
                </div>
              </div>

              <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
                <label style={{ fontSize: 12, color: "#8B7361", fontWeight: 600 }}>Statut</label>
                <select
                  value={statut}
                  onChange={(e) => updateMetaStatut(item.acqId, item.vIndex, e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: `1px solid ${BORDER}`,
                    fontFamily: FF_DM,
                    fontSize: 13,
                    minWidth: 160,
                  }}
                >
                  {STATUTS_RELANCE.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => openModalEmail(item)}
                  style={{
                    padding: "10px 18px",
                    border: "none",
                    borderRadius: 8,
                    background: GOLD,
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FF_DM,
                    fontSize: 13,
                  }}
                >
                  Envoyer rappel email
                </button>
                <button
                  type="button"
                  onClick={() => marquerRecu(item)}
                  style={{
                    padding: "10px 18px",
                    border: "none",
                    borderRadius: 8,
                    background: GREEN,
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FF_DM,
                    fontSize: 13,
                  }}
                >
                  Marquer comme reçu
                </button>
              </div>

              {(m.historique || []).length > 0 && (
                <div style={{ marginTop: 14, padding: 12, background: "#FCFAF7", borderRadius: 8, fontSize: 12, color: "#8B7361" }}>
                  <strong style={{ color: TAUPE }}>Historique des relances</strong>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {(m.historique || []).map((h, i) => (
                      <li key={i}>
                        {new Date(h.date).toLocaleString("fr-FR")} — {h.detail || h.type}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {modal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => !sending && setModal(null)}
          role="presentation"
        >
          <div
            style={{
              background: WHITE,
              borderRadius: 12,
              padding: 24,
              maxWidth: 560,
              width: "100%",
              border: `1px solid ${BORDER}`,
              boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <h3 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Envoyer un rappel</h3>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#8B7361", marginBottom: 4 }}>Destinataire</label>
            <input
              type="email"
              value={sendTo}
              onChange={(e) => setSendTo(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                marginBottom: 12,
                fontFamily: FF_DM,
                fontSize: 14,
                boxSizing: "border-box",
              }}
            />
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#8B7361", marginBottom: 4 }}>Objet</label>
            <input
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                marginBottom: 12,
                fontFamily: FF_DM,
                fontSize: 14,
                boxSizing: "border-box",
              }}
            />
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#8B7361", marginBottom: 4 }}>Corps (HTML)</label>
            <textarea
              value={emailHtml}
              onChange={(e) => setEmailHtml(e.target.value)}
              rows={12}
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                fontFamily: FF_DM,
                fontSize: 12,
                boxSizing: "border-box",
              }}
            />
            {sendError ? <p style={{ color: RED, fontSize: 13, marginTop: 8 }}>{sendError}</p> : null}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
              <button type="button" disabled={sending} onClick={() => setModal(null)} style={{ padding: "10px 16px", borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff", cursor: "pointer" }}>
                Annuler
              </button>
              <button type="button" disabled={sending} onClick={envoyerEmail} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: GOLD, color: "#fff", fontWeight: 700, cursor: sending ? "wait" : "pointer" }}>
                {sending ? "Envoi…" : "Envoyer"}
              </button>
            </div>

            {(meta[metaKey(modal.acqId, modal.vIndex)]?.historique || []).length > 0 && (
              <div style={{ marginTop: 16, fontSize: 12, color: "#8B7361" }}>
                <strong>Historique existant</strong> — les nouvelles relances s’ajouteront après envoi.
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
